import { useCallback, useEffect, useRef } from 'react';
import { useLatestRef } from './useLatestRef';

// Debounced side-effect hook. Returns [schedule, cancel]; subsequent `schedule`
// calls reset the timer. For debounced *values*, use `useDebouncedValue`.
export const useDebouncedCallback = <Args extends unknown[]>(
  callback: (...args: Args) => void,
  delayMs: number
): [(...args: Args) => void, () => void] => {
  // Keep `schedule` ref-stable even as `callback` closes over changing state.
  const callbackRef = useLatestRef(callback);

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const cancel = useCallback(() => {
    if (timeoutRef.current !== undefined) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  }, []);

  useEffect(() => cancel, [cancel]);

  const schedule = useCallback(
    (...args: Args) => {
      cancel();
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = undefined;
        callbackRef.current(...args);
      }, delayMs);
    },
    [callbackRef, cancel, delayMs]
  );

  return [schedule, cancel];
};
